import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from './ui/tooltip';
import { Check, Trash2, FileText, QrCode, Clock, Loader2, Edit, Save, X, RotateCcw } from 'lucide-react';

/**
 * LotesPendentesLista
 * Lista de lotes aguardando aprovação
 */
export default function LotesPendentesLista({
  lotes = [],
  aprovando = null,
  ofId = '',
  handleAprovarLote,
  handleEliminarLote,
  handleGerarPDF,
  openEditObservacoes,
  openHistorico,
  getCategoriaCor,
  onEditLotePendente,
}) {
  const lotesPendentes = lotes.filter(l => l.status === 'pendente');
  const [editingLote, setEditingLote] = useState(null);
  const [editQtd, setEditQtd] = useState('');
  const [editObs, setEditObs] = useState('');
  const [editNome, setEditNome] = useState('');

  const startEdit = (lote) => {
    setEditingLote(lote.id);
    setEditQtd(lote.quantidade || '');
    setEditObs(lote.observacoes || '');
    setEditNome(lote.nome_lote || '');
  };

  const cancelEdit = () => {
    setEditingLote(null);
    setEditQtd('');
    setEditObs('');
    setEditNome('');
  };

  const saveEdit = (loteId) => {
    if (onEditLotePendente) {
      onEditLotePendente(loteId, {
        quantidade: parseInt(editQtd) || 0,
        observacoes: editObs || null,
        nome_lote: editNome || null,
      });
    }
    setEditingLote(null);
  };

  if (!lotesPendentes.length) return null;
  return (
    <>
{/* Lotes Pendentes */}
      {lotesPendentes.length > 0 && (
        <div>
          <h4 className="font-bold text-amber-700 mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Lotes Pendentes ({lotesPendentes.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lotesPendentes.map((lote) => (
              <Card 
                key={lote.id} 
                className="p-4 border-l-4 border-l-amber-500 bg-amber-50/50 hover:shadow-md transition-shadow"
                data-testid={`lote-pendente-${lote.numero_lote}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold text-amber-700">
                        {lote.codigo_lote || `L${lote.numero_lote}`}
                      </span>
                      {lote.categoria_codigo && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getCategoriaCor(lote.categoria_codigo)}`}>
                          {lote.categoria_nome}
                        </span>
                      )}
                    </div>
                    {editingLote === lote.id ? (
                      <div className="space-y-2 mt-2">
                        <div>
                          <label className="text-xs font-medium text-stone-600">Nome do Lote</label>
                          <Input value={editNome} onChange={(e) => setEditNome(e.target.value)} placeholder="Nome (opcional)" className="h-8" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-stone-600">Quantidade</label>
                          <Input type="number" value={editQtd} onChange={(e) => setEditQtd(e.target.value)} className="h-8" min="1" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-stone-600">Observações</label>
                          <Input value={editObs} onChange={(e) => setEditObs(e.target.value)} placeholder="Observações" className="h-8" />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveEdit(lote.id)} className="bg-green-600 hover:bg-green-700 h-7 px-3">
                            <Save className="h-3 w-3 mr-1" /> Guardar
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7 px-3">
                            <X className="h-3 w-3 mr-1" /> Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {lote.nome_lote && <p className="text-sm text-stone-600 font-medium">{lote.nome_lote}</p>}
                        <p className="text-2xl font-bold text-stone-900">{lote.quantidade} <span className="text-sm font-normal">peças</span></p>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {editingLote !== lote.id && (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(lote)} className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" title="Editar">
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleEliminarLote(lote.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {editingLote !== lote.id && (
                  <>
                    <p className="text-xs text-stone-500 mb-3">
                      Criado em {new Date(lote.criado_em).toLocaleString('pt-PT')}
                    </p>
                    
                    {lote.observacoes && (
                      <p className="text-sm text-stone-700 italic mb-3 bg-white p-2 rounded border border-stone-200">{lote.observacoes}</p>
                    )}
                    
                    {(() => {
                      const isArr = lote.categoria_codigo === 'ARR' || lote.origem === 'arranjo';
                      return isArr ? (
                        <div className="flex gap-2">
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleAprovarLote(lote.id)}
                            disabled={aprovando === lote.id}
                            data-testid={`btn-aprovar-lote-${lote.numero_lote}`}
                          >
                            {aprovando === lote.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                            Aprovar
                          </Button>
                          <Button
                            variant="outline"
                            className="border-rose-400 text-rose-700 hover:bg-rose-50"
                            onClick={() => {
                              if (window.confirm(`Resgatar o lote ${lote.codigo_lote || `L${lote.numero_lote}`}?\n\nAs ${lote.quantidade} peças voltam à lista de "Em Arranjo" e poderão ser reagrupadas num novo lote.`)) {
                                handleEliminarLote(lote.id);
                              }
                            }}
                            title="Resgatar: desfaz o lote ARR e devolve as peças à lista de anomalias"
                            data-testid={`btn-resgatar-lote-${lote.numero_lote}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Resgatar
                          </Button>
                        </div>
                      ) : (
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700"
                          onClick={() => handleAprovarLote(lote.id)}
                          disabled={aprovando === lote.id}
                          data-testid={`btn-aprovar-lote-${lote.numero_lote}`}
                        >
                          {aprovando === lote.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Check className="h-4 w-4 mr-2" />
                          )}
                          Aprovar e Gerar PDF
                        </Button>
                      );
                    })()}
                  </>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Lotes Aprovados */}
    </>
  );
}
